// Global Instructions Rule Applied!
//
// One meeting's follow-ups, rendered beneath its minutes (owner ask
// 2026-09-02; the series-wide ledger view and Cancel / Reopen / stale flags
// were retired 2026-09-15). Each row can be ticked done (unticking reopens
// it), edited, removed and turned into a task (owner 2026-09-15: people edit
// the follow-ups; who edited what shows beneath the wording, the original
// wording in a Tooltip).
//
// Read-only/simple table: antd Table size='small' INSIDE the caller's
// FormSection (the CommitmentsTab precedent). Row actions follow the
// word-button grammar (docs/FRONTEND_DESIGN_PATTERNS.md): `Edit`,
// `Create task`, `Remove` (Popconfirm) bordered. The Create-task and Edit
// modals live in the host — this component only reports the row.
import React, { useState } from 'react'
import { Button, Checkbox, Popconfirm, Table, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { PipelineMeetingFollowUp } from '../../../state_manager/data_models/pipeline_meetings_model.ts'

export type FollowUpStatus = 'open' | 'done'

interface FollowUpTableProps {
  rows: PipelineMeetingFollowUp[]
  loading?: boolean
  /** Tick (done) or untick (open) a row. The host applies it optimistically and refetches. */
  onSetStatus: (row: PipelineMeetingFollowUp, status: FollowUpStatus) => Promise<void>
  /** Opens the host's Create-task modal for this row. */
  onCreateTask: (row: PipelineMeetingFollowUp) => void
  /** Opens the host's Edit-follow-up modal for this row. */
  onEdit: (row: PipelineMeetingFollowUp) => void
  /** Soft-deletes the row (the host confirms nothing more — the Popconfirm is here). */
  onRemove: (row: PipelineMeetingFollowUp) => Promise<void>
  emptyText?: string
}

const F = {
  id: 'pipeline_meeting_follow_ups__id',
  description: 'pipeline_meeting_follow_ups__description',
  ownerName: 'pipeline_meeting_follow_ups__owner_name',
  dueDate: 'pipeline_meeting_follow_ups__due_date',
  sourceExcerpt: 'pipeline_meeting_follow_ups__source_excerpt',
  status: 'pipeline_meeting_follow_ups__status',
  statusChangedAt: 'pipeline_meeting_follow_ups__status_changed_at',
  dealId: 'pipeline_meeting_follow_ups__deal_id',
  editedBy: 'pipeline_meeting_follow_ups__edited_by',
  editedAt: 'pipeline_meeting_follow_ups__edited_at',
  originalDescription: 'pipeline_meeting_follow_ups__original_description'
} as const

/** Who ticked it and when — the muted line beneath a done row. */
const doneLine = (row: PipelineMeetingFollowUp): string | null => {
  if (row.prog__effective_status !== 'done') return null
  if (row[F.status] === 'open' && row.tasks__completed) return 'Task completed'
  const parts = ['Done']
  if (row.prog__status_changed_by_name) parts.push(`by ${row.prog__status_changed_by_name}`)
  if (row[F.statusChangedAt]) parts.push(dayjs(row[F.statusChangedAt] as string).format('MM/DD/YYYY'))
  return parts.join(' · ')
}

/** Who added or last edited the row — the muted provenance line (agent
 *  output that nobody touched shows nothing). */
export const editedLine = (row: PipelineMeetingFollowUp): string | null => {
  if (!row[F.editedBy]) return null
  const parts = [row.prog__is_user_added ? 'Added' : 'Edited']
  if (row.prog__edited_by_name) parts.push(`by ${row.prog__edited_by_name}`)
  if (row[F.editedAt]) parts.push(dayjs(row[F.editedAt] as string).format('MM/DD/YYYY'))
  return parts.join(' · ')
}

export const FollowUpTable: React.FC<FollowUpTableProps> = ({
  rows,
  loading = false,
  onSetStatus,
  onCreateTask,
  onEdit,
  onRemove,
  emptyText = 'No follow-ups.'
}) => {
  const { openFormInTab } = useTabManagerContext()
  const [busyId, setBusyId] = useState<string | null>(null)

  const run = async (row: PipelineMeetingFollowUp, status: FollowUpStatus) => {
    setBusyId(row[F.id])
    try {
      await onSetStatus(row, status)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Table
      data-component='FollowUpTable'
      size='small'
      rowKey={F.id}
      loading={loading}
      dataSource={rows}
      pagination={rows.length > 25 ? { pageSize: 25, size: 'small' } : false}
      // Fixed column widths + horizontal scroll: in the narrower minutes card
      // the description must not be squeezed into a one-word column (wide
      // content scrolls inside its own container — design patterns).
      scroll={{ x: 'max-content' }}
      locale={{ emptyText }}
      rowClassName={(row) => (row.prog__effective_status === 'open' ? '' : 'opacity-80')}
      columns={[
        {
          title: '',
          key: 'tick',
          width: 36,
          align: 'center' as const,
          render: (_: unknown, row: PipelineMeetingFollowUp) => {
            const done = row.prog__effective_status === 'done'
            // A follow-up closed by its completed task stays done until the
            // task is reopened — the tick is not the authority there.
            const lockedByTask = done && row[F.status] === 'open' && !!row.tasks__completed
            return (
              <Checkbox
                checked={done}
                disabled={lockedByTask || busyId === row[F.id]}
                aria-label={done ? 'Reopen follow-up' : 'Mark follow-up done'}
                onChange={(e) => run(row, e.target.checked ? 'done' : 'open')}
              />
            )
          }
        },
        {
          title: 'Follow-up',
          key: 'description',
          width: 320,
          render: (_: unknown, row: PipelineMeetingFollowUp) => {
            const done = row.prog__effective_status === 'done'
            const line = doneLine(row)
            const edited = editedLine(row)
            const original = row[F.originalDescription]
            const body = (
              <span className={done ? 'line-through text-gray-500' : 'text-gray-800'}>{row[F.description]}</span>
            )
            return (
              <div className='text-[13px]'>
                {row[F.sourceExcerpt] ? (
                  <Tooltip title={<span className='italic'>“{row[F.sourceExcerpt]}”</span>}>{body}</Tooltip>
                ) : (
                  body
                )}
                {line ? <div className='text-[11.5px] text-gray-400 mt-0.5'>{line}</div> : null}
                {edited ? (
                  <div className='text-[11.5px] text-gray-400 mt-0.5'>
                    {original && original !== row[F.description] ? (
                      <Tooltip title={<span>Originally: “{original}”</span>}>
                        <span className='underline decoration-dotted cursor-help'>{edited}</span>
                      </Tooltip>
                    ) : (
                      edited
                    )}
                  </div>
                ) : null}
              </div>
            )
          }
        },
        {
          title: 'Deal',
          key: 'deal',
          width: 150,
          render: (_: unknown, row: PipelineMeetingFollowUp) => (
            <div className='text-[12.5px]'>
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => openFormInTab('deals', row[F.dealId], row.deals__name || 'Deal')}
              >
                {row.deals__name || 'Deal'}
              </button>
              {row.accounts__name ? <div className='text-[11.5px] text-gray-400'>{row.accounts__name}</div> : null}
            </div>
          )
        },
        {
          title: 'Owner',
          key: 'owner',
          width: 130,
          render: (_: unknown, row: PipelineMeetingFollowUp) =>
            row.owner_contact ? (
              <span className='text-[12.5px]'>{row.owner_contact.prog__display_name || row[F.ownerName]}</span>
            ) : row[F.ownerName] ? (
              <span className='text-[12.5px]'>
                {row[F.ownerName]}{' '}
                <Tooltip title='Not matched to a staff contact — pick the assignee when creating a task, or add a speaker alias in cfg.meeting_speaker_aliases.'>
                  <span className='text-gray-400 text-[11.5px]'>(unmatched)</span>
                </Tooltip>
              </span>
            ) : (
              <span className='text-gray-400 text-[12.5px]'>—</span>
            )
        },
        {
          title: 'Due',
          key: 'due',
          width: 100,
          render: (_: unknown, row: PipelineMeetingFollowUp) =>
            row[F.dueDate] ? (
              <span className='text-[12.5px]'>{dayjs(row[F.dueDate] as string).format('MM/DD/YYYY')}</span>
            ) : (
              <span className='text-gray-400 text-[12.5px]'>—</span>
            )
        },
        {
          title: 'Status',
          key: 'status',
          width: 120,
          render: (_: unknown, row: PipelineMeetingFollowUp) => {
            const done = row.prog__effective_status === 'done'
            return (
              <span className='flex flex-wrap gap-1'>
                <Tag color={done ? 'green' : 'blue'} className='m-0'>
                  {done ? 'Done' : 'Open'}
                </Tag>
                {row.tasks__id ? (
                  <Tooltip title={`Task: ${row.tasks__subject || ''}${row.tasks__completed ? ' (completed)' : ''}`}>
                    <Tag color='green' className='m-0'>
                      Task
                    </Tag>
                  </Tooltip>
                ) : null}
              </span>
            )
          }
        },
        {
          title: '',
          key: 'actions',
          width: 250,
          render: (_: unknown, row: PipelineMeetingFollowUp) => {
            const open = row.prog__effective_status === 'open'
            const busy = busyId === row[F.id]
            return (
              <span className='flex items-center gap-1'>
                {open ? (
                  <Button size='small' disabled={busy} onClick={() => onEdit(row)}>
                    Edit
                  </Button>
                ) : null}
                {open && !row.tasks__id ? (
                  <Button size='small' disabled={busy} onClick={() => onCreateTask(row)}>
                    Create task
                  </Button>
                ) : null}
                {!row.tasks__id ? (
                  <Popconfirm
                    title='Remove this follow-up?'
                    description='It disappears from the minutes; the change is recorded against you.'
                    okText='Remove'
                    okButtonProps={{ danger: true }}
                    onConfirm={async () => {
                      setBusyId(row[F.id])
                      try {
                        await onRemove(row)
                      } finally {
                        setBusyId(null)
                      }
                    }}
                  >
                    <Button size='small' disabled={busy}>
                      Remove
                    </Button>
                  </Popconfirm>
                ) : null}
              </span>
            )
          }
        }
      ]}
    />
  )
}
